import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClientDetailsModal, ClientForm, ClientList } from '../../components/ClientDirectory';
import { ClientsMap } from '../../components/MapView/ClientsMap';
import { clientService, ClientDTO } from '../../services/clientApi';

type AddressValidationListState = {
  status: 'checking' | 'valid' | 'warning' | 'error';
  confidence?: number;
  warning?: string;
};

type AddressValidationCacheEntry = {
  key: string;
  state: AddressValidationListState;
  updatedAt: number;
};

const ADDRESS_VALIDATION_CACHE_STORAGE_KEY = 'clients.address-validation-cache.v1';
const ADDRESS_VALIDATION_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const normalizeAddressPart = (value?: string): string => (value ?? '').trim().toLowerCase();

const buildAddressValidationCacheKey = (client: ClientDTO): string => {
  return [client.address, client.postalCode, client.city, client.country]
    .map((part) => normalizeAddressPart(part))
    .join('|');
};

const readAddressValidationCache = (): Record<string, AddressValidationCacheEntry> => {
  try {
    const raw = localStorage.getItem(ADDRESS_VALIDATION_CACHE_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as Record<string, AddressValidationCacheEntry>;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return parsed;
  } catch {
    return {};
  }
};

const writeAddressValidationCache = (cache: Record<string, AddressValidationCacheEntry>): void => {
  try {
    localStorage.setItem(ADDRESS_VALIDATION_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // No-op: on garde le fonctionnement sans cache si le stockage local échoue.
  }
};

export const Clients: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [pendingReopenClientId, setPendingReopenClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientDTO | null>(null);
  const [viewingClient, setViewingClient] = useState<ClientDTO | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [allGeocodedClients, setAllGeocodedClients] = useState<ClientDTO[]>([]);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [addressValidationStatusByClientId, setAddressValidationStatusByClientId] = useState<Record<string, AddressValidationListState>>({});

  const limit = 20;

  const loadClients = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await clientService.getClients({
        page: currentPage,
        limit,
        search: searchQuery || undefined,
        active: filterActive,
      });

      if (response.success) {
        setClients(response.data);
        setTotalClients(response.meta?.total || 0);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des clients:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des clients');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [currentPage, searchQuery, filterActive]);

  // Charge tous les clients géolocalisés quand on passe en vue carte
  useEffect(() => {
    if (viewMode !== 'map') return;
    const loadAllForMap = async () => {
      setIsLoadingMap(true);
      try {
        const response = await clientService.getClients({ page: 1, limit: 1000 });
        if (response.success) {
          setAllGeocodedClients(response.data);
        }
      } catch (err) {
        console.error('Erreur chargement carte:', err);
      } finally {
        setIsLoadingMap(false);
      }
    };
    loadAllForMap();
  }, [viewMode]);

  // Vérification d'adresse non bloquante pour afficher l'état dans la liste.
  useEffect(() => {
    if (viewMode !== 'list') {
      return;
    }

    const clientsWithId = clients.filter((client) => Boolean(client.id));
    if (clientsWithId.length === 0) {
      setAddressValidationStatusByClientId({});
      return;
    }

    let isCancelled = false;
    const now = Date.now();
    const cache = readAddressValidationCache();

    const clientsToVerify: ClientDTO[] = [];

    const initialStates = clientsWithId.reduce<Record<string, AddressValidationListState>>((accumulator, client) => {
      const clientId = client.id!;
      const cacheEntry = cache[clientId];
      const currentAddressKey = buildAddressValidationCacheKey(client);
      const isFresh = Boolean(cacheEntry) && now - cacheEntry.updatedAt < ADDRESS_VALIDATION_CACHE_TTL_MS;
      const isSameAddress = cacheEntry?.key === currentAddressKey;

      if (isFresh && isSameAddress && cacheEntry?.state.status !== 'checking') {
        accumulator[clientId] = cacheEntry.state;
      } else {
        accumulator[clientId] = { status: 'checking' };
        clientsToVerify.push(client);
      }

      return accumulator;
    }, {});

    setAddressValidationStatusByClientId(initialStates);

    if (clientsToVerify.length === 0) {
      return;
    }

    const verifyClientsAddresses = async () => {
      for (const client of clientsToVerify) {
        if (isCancelled || !client.id) {
          return;
        }

        const clientId = client.id;
        let nextState: AddressValidationListState;

        try {
          const response = await clientService.validateAddress({
            address: client.address,
            postalCode: client.postalCode,
            city: client.city,
            country: client.country,
          });

          nextState = response.data?.isValid
            ? {
                status: 'valid',
                confidence: response.data.confidence,
              }
            : {
                status: 'warning',
                confidence: response.data?.confidence,
                warning: response.data?.warning,
              };
        } catch {
          nextState = {
            status: 'error',
            warning: 'Impossible de vérifier cette adresse',
          };
        }

        if (isCancelled) {
          return;
        }

        cache[clientId] = {
          key: buildAddressValidationCacheKey(client),
          state: nextState,
          updatedAt: Date.now(),
        };
        writeAddressValidationCache(cache);

        setAddressValidationStatusByClientId((previous) => ({
          ...previous,
          [clientId]: nextState,
        }));
      }
    };

    verifyClientsAddresses();

    return () => {
      isCancelled = true;
    };
  }, [clients, viewMode]);

  // Détecter le retour depuis la page Agenda après création d'un rendez-vous
  useEffect(() => {
    const navigationState = location.state as { reopenClientId?: string } | null;
    if (!navigationState?.reopenClientId) {
      return;
    }
    setPendingReopenClientId(navigationState.reopenClientId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // Rouvrir la fiche client dès que la liste est disponible
  useEffect(() => {
    if (!pendingReopenClientId || clients.length === 0) {
      return;
    }
    const clientToReopen = clients.find((c) => c.id === pendingReopenClientId);
    if (clientToReopen) {
      setViewingClient(clientToReopen);
      setPendingReopenClientId(null);
    }
  }, [pendingReopenClientId, clients]);

  const handleSaveClient = async (clientData: Omit<ClientDTO, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingClient?.id) {
        await clientService.updateClient(editingClient.id, clientData);
      } else {
        await clientService.createClient(clientData);
      }
      setShowModal(false);
      setEditingClient(null);
      await loadClients();
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde du client:', err);
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde du client');
    }
  };

  const handleDeleteClient = async (clientId: string): Promise<boolean> => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      return false;
    }

    try {
      await clientService.deleteClient(clientId);
      await loadClients();
      return true;
    } catch (err: any) {
      console.error('Erreur lors de la suppression du client:', err);
      alert(err.response?.data?.message || 'Erreur lors de la suppression du client');
      return false;
    }
  };

  const handleEditClient = (client: ClientDTO) => {
    setViewingClient(null);
    setEditingClient(client);
    setShowModal(true);
  };

  const handleCreateClient = () => {
    setViewingClient(null);
    setEditingClient(null);
    setShowModal(true);
  };

  const handleViewClient = (client: ClientDTO) => {
    setViewingClient(client);
  };

  const handleScheduleClient = (client: ClientDTO) => {
    if (!client.id) {
      return;
    }

    const startTime = new Date();
    const duration = client.defaultVisitDuration || 60;
    const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

    setViewingClient(null);
    navigate('/schedule', {
      state: {
        openNewAppointment: true,
        clientId: client.id,
        clientName: `${client.firstName} ${client.lastName}`,
        duration,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      }
    });
  };

  const handleDeleteClientFromDetails = async (client: ClientDTO) => {
    if (!client.id) {
      return;
    }

    const deleted = await handleDeleteClient(client.id);

    if (deleted) {
      setViewingClient(null);
    }
  };

  const totalPages = Math.ceil(totalClients / limit);
  let activeFilterValue = 'all';

  if (filterActive === true) {
    activeFilterValue = 'active';
  } else if (filterActive === false) {
    activeFilterValue = 'inactive';
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-500">Gérez vos clients et leurs informations</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle liste / carte */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Vue liste"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Liste
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                viewMode === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Vue carte"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Carte
            </button>
          </div>

          <button
            onClick={handleCreateClient}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nouveau client
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Rechercher par nom, email, ville..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <select
              value={activeFilterValue}
              onChange={(e) => {
                const value = e.target.value;
                setFilterActive(value === 'all' ? undefined : value === 'active');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les clients</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="font-medium">{totalClients} clients au total</span>
          {searchQuery && (
            <span className="text-blue-600">
              {clients.length} résultat(s) pour "{searchQuery}"
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {viewMode === 'list' ? (
        <ClientList
          clients={clients}
          onView={handleViewClient}
          isLoading={isLoading}
          addressValidationStatusByClientId={addressValidationStatusByClientId}
        />
      ) : (
        <ClientsMap
          clients={allGeocodedClients}
          onViewClient={handleViewClient}
          isLoading={isLoadingMap}
        />
      )}

      <ClientDetailsModal
        client={viewingClient}
        isOpen={viewingClient !== null}
        onClose={() => setViewingClient(null)}
        onEdit={handleEditClient}
        onDelete={handleDeleteClientFromDetails}
        onSchedule={handleScheduleClient}
      />

      {viewMode === 'list' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Précédent
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Suivant
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </h2>
              <ClientForm
                client={editingClient}
                onSubmit={handleSaveClient}
                onCancel={() => {
                  setShowModal(false);
                  setEditingClient(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
